// repo/primary-care-connect/frontend/src/api/ohtAdmin.ts
//
// OHT Admin Portal API client.
// Uses live backend endpoints only.
// Compatibility fields are kept temporarily so the current OHT pages compile
// while we update each page one file at a time.

import { ohtScopeHeaders } from "../utils/portalRoleChoice";

export class OhtApiError extends Error {
  status: number;
  body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "OhtApiError";
    this.status = status;
    this.body = body;
  }
}

type ApiEnvelope<T> = {
  ok: boolean;
  result?: T;
  error?: string;
};

async function parseJsonSafe(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
      ...ohtScopeHeaders(),
    },
    credentials: "include",
  });

  if (res.ok) {
    if (res.status === 204) return null as unknown as T;
    return (await parseJsonSafe(res)) as T;
  }

  const body = await parseJsonSafe(res);

  const msg =
    body && typeof body === "object" && "error" in body
      ? String((body as any).error)
      : `${res.status} ${res.statusText}`;

  throw new OhtApiError(res.status, msg, body);
}

async function requestResult<T>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const body = await request<ApiEnvelope<T>>(path, init);

  if (!body || body.ok !== true || body.result === undefined) {
    throw new OhtApiError(500, body?.error || "Invalid API response", body);
  }

  return body.result;
}

function get<T>(path: string, signal?: AbortSignal) {
  return request<T>(path, { method: "GET", signal });
}

function getResult<T>(path: string, signal?: AbortSignal) {
  return requestResult<T>(path, { method: "GET", signal });
}

function postResult<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
) {
  return requestResult<T>(path, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
    signal,
  });
}

function patchResult<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
) {
  return requestResult<T>(path, {
    method: "PATCH",
    body: JSON.stringify(body ?? {}),
    signal,
  });
}

function deleteResult<T>(path: string, signal?: AbortSignal) {
  return requestResult<T>(path, {
    method: "DELETE",
    signal,
  });
}

function buildQuery(params: Record<string, string | number | null | undefined>) {
  const usp = new URLSearchParams();

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }

  const s = usp.toString();
  return s ? `?${s}` : "";
}

// -----------------------------------------------------------------------------
// Bootstrap
// -----------------------------------------------------------------------------

export type OhtBootstrapRole = {
  roleName: string;
  clinicId: number | null;
  regionId: number | null;
  ohtId?: number | string | null;
  ohtName?: string | null;
  ohtCode?: string | null;
  isActive?: boolean | number | null;
};

export type OhtBootstrapResponse = {
  userId: number;
  status: string;
  roles?: OhtBootstrapRole[];
  primaryRole?: string | null;
};

export function getBootstrap(signal?: AbortSignal) {
  return request<OhtBootstrapResponse>("/api/portal-auth-bootstrap", {
    method: "POST",
    signal,
  });
}

// -----------------------------------------------------------------------------
// Existing analytics endpoints
// -----------------------------------------------------------------------------

export type LeadsBreakdownItem = {
  key: string;
  label: string;
  count: number;
  percent?: number;
};

export type LeadsAggregate = {
  total: number;
  sex: LeadsBreakdownItem[];
  ageGroups: LeadsBreakdownItem[];
};

export type LeadsScopeDoctor = {
  id: number;
  name: string;
  clinicId: number | null;
  acceptingNewPatients?: boolean;
};

export type LeadsResponse = {
  filters: {
    from: string | null;
    to: string | null;
    baseline: string;
  };
  applied: LeadsAggregate;
  attached: LeadsAggregate;
  meta: {
    role: string;
    scope: {
      regionId: number | null;
      ohtId: number | null;
      clinicIds: number[];
      doctors: LeadsScopeDoctor[] | null;
      acceptingDoctors?: number;
      totalDoctors?: number;
    };
  };
};

export type LeadsParams = {
  from?: string | null;
  to?: string | null;
  baseline?: "all" | "unattached" | "attached";
  clinicId?: number | null;
  doctorIds?: number[] | null;
};

function serializeDoctorIds(ids: number[] | null | undefined) {
  if (!ids || ids.length === 0) return undefined;
  return ids.join(",");
}

export function getLeadsAnalytics(params: LeadsParams, signal?: AbortSignal) {
  const qs = buildQuery({
    from: params.from ?? undefined,
    to: params.to ?? undefined,
    baseline: params.baseline ?? undefined,
    clinicId: params.clinicId ?? undefined,
    doctorIds: serializeDoctorIds(params.doctorIds),
  });

  return get<LeadsResponse>(`/api/portal/patient-leads-analytics${qs}`, signal);
}

export type AttachmentsSeries = {
  key: string;
  label: string;
  data: number[];
};

export type AttachmentsResponse = {
  months: string[];
  series: AttachmentsSeries[];
  meta: {
    year: number;
    groupBy: "none" | "doctor" | "clinic";
    baseline: string;
    role: string;
    scope: {
      regionId: number | null;
      ohtId: number | null;
      clinicIds: number[];
      doctors: LeadsScopeDoctor[] | null;
    };
  };
};

export type AttachmentsParams = {
  year: number;
  baseline?: "all" | "unattached" | "attached";
  groupBy?: "none" | "doctor" | "clinic";
  clinicId?: number | null;
  doctorIds?: number[] | null;
};

export function getAttachmentsAnalytics(
  params: AttachmentsParams,
  signal?: AbortSignal
) {
  const qs = buildQuery({
    year: params.year,
    baseline: params.baseline ?? undefined,
    groupBy: params.groupBy ?? undefined,
    clinicId: params.clinicId ?? undefined,
    doctorIds: serializeDoctorIds(params.doctorIds),
  });

  return get<AttachmentsResponse>(
    `/api/portal/attachments-analytics${qs}`,
    signal
  );
}

export type TimeToAttachmentScope = "oht" | "fsa" | "clinic" | "doctor" | "city";

export type TimeToAttachmentResponse = {
  ok: boolean;
  metric: "timeToAttachment";
  filters: {
    scopeType: TimeToAttachmentScope | string;
    scopeId: number | string | null;
    startDate: string | null;
    endDate: string | null;
    verifiedOnly: boolean;
    attachedOnly: boolean;
  };
  result: {
    scopeType: string;
    scopeId: number | string | null;
    completedVerifiedCount: number;
    averageDaysToAttachment: number | null;
    earliestSubmittedAt: string | null;
    latestAttachmentCompletedAt: string | null;
  };
};

export type TimeToAttachmentParams = {
  scopeType: TimeToAttachmentScope;
  scopeId: number | string;
  startDate?: string | null;
  endDate?: string | null;
};

export function getTimeToAttachment(
  params: TimeToAttachmentParams,
  signal?: AbortSignal
) {
  const qs = buildQuery({
    scopeType: params.scopeType,
    scopeId: params.scopeId,
    startDate: params.startDate ?? undefined,
    endDate: params.endDate ?? undefined,
  });

  return get<TimeToAttachmentResponse>(
    `/api/portal/time-to-attachment${qs}`,
    signal
  );
}

// -----------------------------------------------------------------------------
// Shared lookups
// -----------------------------------------------------------------------------

export type City = {
  CityID: number;
  Name: string;
  RegionID: number | null;
};

export function getCities(signal?: AbortSignal) {
  return get<City[]>("/api/cities", signal);
}

export type ClinicListItem = {
  ClinicID: number;
  Name: string;
  AddressLine1?: string | null;
  AddressLine2?: string | null;
  City?: string | null;
  Province?: string | null;
  PostalCode?: string | null;
  NormalizedFSA?: string | null;
};

export function getClinicsList(signal?: AbortSignal) {
  return get<ClinicListItem[] | { items: ClinicListItem[] }>(
    "/api/clinics",
    signal
  );
}

// -----------------------------------------------------------------------------
// OHT Admin profile
// -----------------------------------------------------------------------------

export type OhtActiveAdmin = {
  userRoleId: number;
  userId: number;
  displayName: string | null;
  email: string;
  activatedAt: string | null;
};

export type OhtProfileCurrentUser = {
  userId: number;
  email: string;
  displayName: string | null;
  role: "OHTAdmin";
  userRoleId: number;
};

export type OhtProfile = {
  ohtId: number;
  name: string;
  code: string;

  // Compatibility fields used by current OHT profile UI.
  // These remain read-only/display-only until Super Admin OHT profile editing exists.
  region?: string | null;
  lead?: string | null;
  established?: string | null;

  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;

  fsas: string[];

  clinicCount: number;
  doctorCount: number;
  acceptingDoctorCount: number;

  activeAdminCount: number;
  activeAdmins: OhtActiveAdmin[];

  currentUser: OhtProfileCurrentUser;
};

export async function getOhtProfile(signal?: AbortSignal): Promise<OhtProfile> {
  const profile = await getResult<OhtProfile>(
    "/api/portal/oht-admin/profile",
    signal
  );

  return {
    ...profile,
    region: profile.region ?? null,
    lead: profile.lead ?? null,
    established: profile.established ?? profile.createdAt ?? null,
  };
}

// Compatibility type used by the current OHT Profile page.
export type OhtAdminUser = {
  id: number;
  name: string;
  email: string;
  role: "Owner" | "Admin";
  lastActiveAt: string | null;
};

export type OhtAdminsResult = {
  items: OhtAdminUser[];
};

// Compatibility wrapper for existing OHT pages still importing getOhtAdmins.
// Internally uses the live OHT profile endpoint.
export async function getOhtAdmins(
  signal?: AbortSignal
): Promise<OhtAdminsResult> {
  const profile = await getOhtProfile(signal);

  return {
    items: (profile.activeAdmins || []).map((admin, index) => ({
      id: admin.userId,
      name: admin.displayName || admin.email || `OHT admin ${admin.userId}`,
      email: admin.email,
      role: index === 0 ? "Owner" : "Admin",
      lastActiveAt: admin.activatedAt,
    })),
  };
}

// -----------------------------------------------------------------------------
// OHT Admin clinics
// -----------------------------------------------------------------------------

export type OhtSummary = {
  ohtId: number;
  name: string;
  code: string;
};

export type OhtClinicsTotals = {
  clinicCount: number;
  doctorCount: number;
  acceptingDoctorCount: number;
  declaredCapacity: number;
  usedCapacity: number;
  remainingCapacity: number;
  applications30d: number;
  attachments30d: number;
  pipelineCount: number;
};

export type OhtClinic = {
  clinicId: number;
  clinicName: string;

  addressLine1: string | null;
  addressLine2: string | null;
  cityId: number | null;
  cityName: string | null;
  postalCode: string | null;
  normalizedFsa: string | null;

  phone: string | null;
  fax: string | null;
  website: string | null;

  latitude: number | null;
  longitude: number | null;

  governanceStatus: string;
  isPublicEligible: boolean;

  doctorCount: number;
  acceptingDoctorCount: number;

  declaredCapacity: number;
  usedCapacity: number;
  remainingCapacity: number;

  applications30d: number;
  attachments30d: number;
  pipelineCount: number;

  avgDaysToAttachment: number | null;

  latestCapacityUpdatedAt: string | null;
  latestAcceptingStatusUpdatedAt: string | null;
};

export type OhtClinicsResult = {
  oht: OhtSummary;
  totals: OhtClinicsTotals;
  items: OhtClinic[];
};

export function getOhtClinics(signal?: AbortSignal) {
  return getResult<OhtClinicsResult>("/api/portal/oht-admin/clinics", signal);
}

// -----------------------------------------------------------------------------
// OHT Admin doctors
// -----------------------------------------------------------------------------

export type OhtDoctorsFilter = {
  clinicId: number | null;
};

export type OhtDoctorsTotals = {
  doctorCount: number;
  acceptingDoctorCount: number;
  declaredCapacity: number;
  usedCapacity: number;
  remainingCapacity: number;
  applications30d: number;
  attachments30d: number;
  pipelineCount: number;
  acceptedPendingAttachmentCount: number;
};

export type OhtDoctor = {
  familyDoctorId: number;
  userId: number | null;

  fullName: string;
  gender: string | null;
  cpso: string | null;

  clinicId: number;
  clinicName: string;
  cityId: number | null;
  cityName: string | null;
  postalCode: string | null;
  normalizedFsa: string | null;

  acceptingNewPatients: boolean;
  acceptingStatusUpdatedAt: string | null;

  intakeNotes: string | null;
  firstAppointmentBookingUrl: string | null;

  declaredCapacity: number | null;
  remainingCapacityOverride: number | null;
  remainingCapacityUpdatedAt: string | null;

  usedCapacity: number;
  remainingCapacity: number | null;

  applications30d: number;
  attachments30d: number;
  pipelineCount: number;
  acceptedPendingAttachmentCount: number;

  avgDaysToAttachment: number | null;
  lastAttachmentCompletedAt: string | null;
  lastRequestSubmittedAt: string | null;

  languages: string[];
  visitTypes: string[];
  coverageTypes: string[];
};

export type OhtDoctorsResult = {
  oht: OhtSummary;
  filter: OhtDoctorsFilter;
  totals: OhtDoctorsTotals;
  items: OhtDoctor[];
};

export type OhtDoctorsParams = {
  clinicId?: number | null;
};

export function getOhtDoctors(
  params: OhtDoctorsParams = {},
  signal?: AbortSignal
) {
  const qs = buildQuery({
    clinicId: params.clinicId ?? undefined,
  });

  return getResult<OhtDoctorsResult>(
    `/api/portal/oht-admin/doctors${qs}`,
    signal
  );
}

// -----------------------------------------------------------------------------
// OHT Admin insights / reports
// -----------------------------------------------------------------------------

export type OhtPatientBaselineStatus = "all" | "unattached" | "attached";

export type OhtInsightsParams = {
  from?: string | null;
  to?: string | null;
  cityId?: number | null;
  city?: string | null;
  fsa?: string | null;
  baselineStatus?: OhtPatientBaselineStatus | null;
};

export type OhtInsightsFilter = {
  from: string | null;
  to: string;
  cityId: number | null;
  city: string | null;
  fsa: string | null;
  baselineStatus: OhtPatientBaselineStatus | string;
};

export type OhtCapacityInsights = {
  doctorCount: number;
  acceptingDoctorCount: number;
  declaredTotal: number;
  usedTotal: number;
  remainingTotal: number;
  missingDeclaredCapacityCount: number;
  staleCapacityCount: number;
  oldestCapacityUpdatedAt: string | null;
  latestCapacityUpdatedAt: string | null;
};

export type OhtThroughputInsights = {
  applicationsInRange: number;
  applications30d: number;
  attachmentsInRange: number;
  attachments30d: number;
  pacePerDay: number;
  avgDaysToAttachment: number | null;
};

export type OhtPipelineInsights = {
  submitted: number;
  inReview: number;
  waitlist: number;
  acceptedPendingAttachment: number;
  totalActive: number;
};

export type OhtClosureInsights = {
  declined: number;
  cancelled: number;
  expired: number;
};

export type OhtTargetBasis = "OHT" | "FSA" | "None" | string;

export type OhtTargetInsights = {
  targetBasis?: OhtTargetBasis;
  activeFsaTargetCount?: number;
  activeOhtTargetCount?: number;
  activeTargetCount: number;
  activeTargetPatients: number;
  completedAgainstTarget: number;
  gap: number;
  estimatedDaysToTarget: number | null;
};

export type OhtReportWindow = {
  from: string;
  to: string;
  days: number;
};

export type OhtReportAcceptingStatus = "Yes" | "No" | "Mixed" | string;

export type OhtReportRow = {
  fsa: string | null;
  cityId: number | null;
  city: string | null;

  clinicId: number;
  clinic: string;

  doctors: number;
  doctorNames: string;

  accepting: OhtReportAcceptingStatus;
  acceptingDoctorCount: number;

  requestCount: number;
  attachedCount: number;
  pipelineCount: number;

  pacePerDay: number | null;
  avgDaysToAttachment: number | null;

  declaredCapacity: number;
  remainingCapacity: number;
  usedCapacity: number;

  missingDeclaredCapacityCount: number;
  staleCapacityCount: number;

  acceptingStatusUpdatedAt: string | null;
  capacityUpdatedAt: string | null;

  targetLevel: OhtTargetBasis;
  targetPatients: number | null;
  completedAgainstTarget: number | null;
  gapVsTarget: number | null;
  estimatedDaysToTarget: number | null;
};

export type OhtFsaInsights = {
  fsa: string;
  fsaLabel: string;
  clinicCount: number;
  doctorCount: number;
  acceptingDoctorCount: number;
  declaredCapacity: number;
  usedCapacity: number;
  remainingCapacity: number;
  staleCapacityCount: number;
  applications: number;
  attachments: number;
  pipelineCount: number;
  avgDaysToAttachment: number | null;
  targetPatients: number;
  gapVsTarget: number;
};

export type OhtCityInsights = {
  cityId: number | null;
  cityName: string | null;
  clinicCount: number;
  doctorCount: number;
  acceptingDoctorCount: number;
  declaredCapacity: number;
  usedCapacity: number;
  remainingCapacity: number;
  staleCapacityCount: number;
  applications: number;
  attachments: number;
  pipelineCount: number;
  avgDaysToAttachment: number | null;
  targetPatients: number;
  gapVsTarget: number;
};

export type OhtInsightsResult = {
  oht: OhtSummary;
  filter: OhtInsightsFilter;
  capacity: OhtCapacityInsights;
  throughput: OhtThroughputInsights;
  pipeline: OhtPipelineInsights;
  closures: OhtClosureInsights;
  targets: OhtTargetInsights;
  reportWindow?: OhtReportWindow;
  reportRows?: OhtReportRow[];
  fsas: OhtFsaInsights[];
  cities: OhtCityInsights[];
};

export function getOhtInsights(
  params: OhtInsightsParams = {},
  signal?: AbortSignal
) {
  const qs = buildQuery({
    from: params.from ?? undefined,
    to: params.to ?? undefined,
    cityId: params.cityId ?? undefined,
    city: params.city ?? undefined,
    fsa: params.fsa ?? undefined,
    baselineStatus: params.baselineStatus ?? undefined,
  });

  return getResult<OhtInsightsResult>(
    `/api/portal/oht-admin/insights${qs}`,
    signal
  );
}

// -----------------------------------------------------------------------------
// OHT Admin targets
// -----------------------------------------------------------------------------

export type OhtTargetLevel = "OHT" | "FSA" | "City" | "Clinic";
export type ActiveOhtTargetLevel = "OHT" | "FSA";
export type EditableOhtTargetLevel = "FSA";

// Current OHT pages use these UI status values.
// Live backend values are normalized into these values below.
export type OhtTargetStatus = "complete" | "on-track" | "at-risk" | "stalled";

export type OhtTarget = {
  ohtTargetId: number;
  targetLevel: OhtTargetLevel;

  // Compatibility fields used by current OHT Overview/Profile pages.
  scope: OhtTargetLevel;
  name: string;

  fsa: string | null;
  fsaLabel: string | null;

  // Compatibility fields retained while old UI files are updated.
  cityId: number | null;
  cityName: string | null;

  clinicId: number | null;
  clinicName: string | null;

  scopeLabel: string;

  targetPatients: number;
  startDate: string;
  endDate: string;

  completed: number;
  requests: number;
  gap: number;

  percentComplete: number | null;
  pacePerDay: number | null;
  estimatedDaysToTarget: number | null;

  attachments30d: number;
  status: OhtTargetStatus;

  createdAt: string;
  createdByUserId: number;
  createdByDisplayName: string | null;
  createdByEmail: string | null;
};

export type OhtTargetsResult = {
  oht: OhtSummary;
  items: OhtTarget[];
};

export type CreateOhtTargetInput = {
  targetLevel: EditableOhtTargetLevel;
  fsa: string;

  // Legacy fields retained only so not-yet-updated UI files still compile.
  // They are rejected by the API client and backend when present.
  cityId?: number | null;
  clinicId?: number | null;

  targetPatients: number;
  startDate: string;
  endDate?: string | null;
};

export type UpdateOhtTargetInput = Partial<CreateOhtTargetInput>;

export type DeleteOhtTargetResult = {
  deleted: boolean;
  ohtTargetId: number;
  targetLevel: ActiveOhtTargetLevel;
  fsa: string | null;
};

function normalizeFsa(value: unknown): string | null {
  if (value === undefined || value === null) return null;

  const text = String(value).trim().replace(/\s+/g, "").toUpperCase();
  return text || null;
}

function requireFsa(value: unknown, body: unknown): string {
  const fsa = normalizeFsa(value);

  if (!fsa) {
    throw new OhtApiError(400, "fsa is required for FSA targets", body);
  }

  if (!/^[A-Z0-9]{3}$/.test(fsa)) {
    throw new OhtApiError(
      400,
      "fsa must be a valid 3-character FSA",
      body
    );
  }

  return fsa;
}

function assertNonNegativeInteger(value: unknown, body: unknown): number {
  if (value === undefined || value === null || String(value).trim() === "") {
    throw new OhtApiError(
      400,
      "targetPatients is required and must be a whole number of 0 or greater",
      body
    );
  }

  const n = Number(value);

  if (!Number.isInteger(n) || n < 0) {
    throw new OhtApiError(
      400,
      "targetPatients must be a whole number of 0 or greater",
      body
    );
  }

  return n;
}

function normalizeOhtTargetLevel(value: unknown): ActiveOhtTargetLevel {
  const text = String(value || "").trim().toUpperCase();

  if (text === "FSA") return "FSA";
  return "OHT";
}

function normalizeTargetStatus(target: Partial<OhtTarget> & any): OhtTargetStatus {
  const serverStatus = String(target.status || "").trim().toLowerCase();
  const gap = Number(target.gap ?? target.targetPatients ?? 0) || 0;

  if (serverStatus === "complete" || serverStatus === "achieved") return "complete";
  if (serverStatus === "stalled" || serverStatus === "ended") return "stalled";
  if (serverStatus === "at-risk" || serverStatus === "atrisk") return "at-risk";
  if (serverStatus === "on-track" || serverStatus === "ontrack" || serverStatus === "upcoming") {
    return "on-track";
  }

  if (gap <= 0) return "complete";
  if (target.estimatedDaysToTarget == null && gap > 0) return "stalled";

  return "on-track";
}

function normalizeOhtTarget(raw: any): OhtTarget {
  const targetLevel = normalizeOhtTargetLevel(raw?.targetLevel || raw?.scope || "OHT");
  const fsa = normalizeFsa(raw?.fsa);
  const fsaLabel = raw?.fsaLabel || (fsa ? `FSA ${fsa}` : null);

  const scopeLabel =
    raw?.scopeLabel ||
    fsaLabel ||
    raw?.cityName ||
    raw?.clinicName ||
    "OHT";

  const targetPatients = Number(raw?.targetPatients || 0);
  const completed = Number(raw?.completed || 0);
  const gap =
    raw?.gap === null || raw?.gap === undefined
      ? Math.max(0, targetPatients - completed)
      : Math.max(0, Number(raw.gap || 0));

  return {
    ...raw,
    ohtTargetId: Number(raw?.ohtTargetId || raw?.id || 0),
    targetLevel,
    scope: targetLevel,
    name: raw?.name || `${scopeLabel} attachment target`,
    fsa,
    fsaLabel,
    cityId: raw?.cityId ?? null,
    cityName: raw?.cityName ?? null,
    clinicId: raw?.clinicId ?? null,
    clinicName: raw?.clinicName ?? null,
    scopeLabel,
    targetPatients,
    startDate: raw?.startDate || "",
    endDate: raw?.endDate || "",
    completed,
    requests: Number(raw?.requests || 0),
    gap,
    percentComplete:
      raw?.percentComplete === null || raw?.percentComplete === undefined
        ? targetPatients > 0
          ? Math.round((completed / targetPatients) * 100)
          : 0
        : Number(raw.percentComplete),
    pacePerDay:
      raw?.pacePerDay === null || raw?.pacePerDay === undefined
        ? null
        : Number(raw.pacePerDay),
    estimatedDaysToTarget:
      raw?.estimatedDaysToTarget === null ||
      raw?.estimatedDaysToTarget === undefined
        ? null
        : Number(raw.estimatedDaysToTarget),
    attachments30d: Number(raw?.attachments30d || 0),
    status: normalizeTargetStatus({ ...raw, gap, targetPatients, completed }),
    createdAt: raw?.createdAt || "",
    createdByUserId: Number(raw?.createdByUserId || 0),
    createdByDisplayName: raw?.createdByDisplayName ?? null,
    createdByEmail: raw?.createdByEmail ?? null,
  };
}

function toApiTargetInput(
  body: CreateOhtTargetInput | UpdateOhtTargetInput,
  options: { requireTargetLevel: boolean }
) {
  const hasTargetLevel =
    body.targetLevel !== undefined &&
    body.targetLevel !== null &&
    String(body.targetLevel).trim() !== "";

  if (options.requireTargetLevel && !hasTargetLevel) {
    throw new OhtApiError(400, "targetLevel is required", body);
  }

  if (hasTargetLevel) {
    const targetLevelText = String(body.targetLevel || "").trim().toUpperCase();

    if (targetLevelText !== "FSA") {
      throw new OhtApiError(
        400,
        "Only FSA targets can be saved. The whole-OHT target is calculated from the total of all FSA targets.",
        body
      );
    }
  }

  if (body.cityId !== undefined && body.cityId !== null && String(body.cityId).trim() !== "") {
    throw new OhtApiError(
      400,
      "City targets are no longer supported. Use FSA targets only.",
      body
    );
  }

  if (body.clinicId !== undefined && body.clinicId !== null && String(body.clinicId).trim() !== "") {
    throw new OhtApiError(
      400,
      "Clinic targets are no longer supported. Use FSA targets only.",
      body
    );
  }

  const payload: Record<string, unknown> = {};

  if (hasTargetLevel) {
    payload.targetLevel = "FSA";
  }

  if (body.targetPatients !== undefined) {
    payload.targetPatients = assertNonNegativeInteger(body.targetPatients, body);
  }

  if (body.startDate !== undefined) {
    payload.startDate = body.startDate;
  }

  if (body.endDate !== undefined) {
    payload.endDate = body.endDate ?? null;
  }

  if (body.fsa !== undefined || hasTargetLevel) {
    payload.fsa = requireFsa(body.fsa, body);
  }

  return payload;
}

export async function getOhtTargets(
  signal?: AbortSignal
): Promise<OhtTargetsResult> {
  const result = await getResult<OhtTargetsResult>(
    "/api/portal/oht-admin/targets",
    signal
  );

  return {
    ...result,
    items: (result.items || []).map(normalizeOhtTarget),
  };
}

export async function createOhtTarget(
  body: CreateOhtTargetInput,
  signal?: AbortSignal
): Promise<OhtTarget> {
  const target = await postResult<OhtTarget>(
    "/api/portal/oht-admin/targets",
    toApiTargetInput(body, { requireTargetLevel: true }),
    signal
  );

  return normalizeOhtTarget(target);
}

export async function updateOhtTarget(
  targetId: number,
  body: UpdateOhtTargetInput,
  signal?: AbortSignal
): Promise<OhtTarget> {
  const target = await patchResult<OhtTarget>(
    `/api/portal/oht-admin/targets/${targetId}`,
    toApiTargetInput(body, { requireTargetLevel: false }),
    signal
  );

  return normalizeOhtTarget(target);
}

export async function deleteOhtTarget(
  targetId: number,
  signal?: AbortSignal
): Promise<DeleteOhtTargetResult> {
  return deleteResult<DeleteOhtTargetResult>(
    `/api/portal/oht-admin/targets/${targetId}`,
    signal
  );
}

// -----------------------------------------------------------------------------
// OHT Admin messages
// -----------------------------------------------------------------------------

export type OhtMessageType =
  | "Announcement"
  | "PolicyUpdate"
  | "CapacityRequest"
  | "OperationalNotice";

export type OhtMessageTargetScope =
  | "AllClinicsInOHT"
  | "AcceptingClinicsOnly"
  | "SelectedClinics";

// Current OHT pages use these UI values.
// Live backend values are normalized into these values below.
export type OhtMessageUiType =
  | "announcement"
  | "policy_update"
  | "capacity_request"
  | "operational_notice";

export type OhtMessageUiScope = "all" | "accepting" | "selected";

export type OhtMessage = {
  ohtMessageId: number;
  ohtId: number;

  // Compatibility fields used by current OHT Messages/Overview pages.
  type: OhtMessageUiType;
  scope: OhtMessageUiScope;
  sentAt: string;
  recipientsCount: number;

  messageType: OhtMessageType;
  title: string;
  body: string;
  targetScope: OhtMessageTargetScope;

  requiresAcknowledgement: boolean;
  expiresAt: string | null;
  isActive: boolean;

  createdAt: string;
  createdByUserId: number;
  createdByDisplayName: string | null;
  createdByEmail: string | null;

  recipientCount: number;
  acknowledgedCount: number;
  responseCount: number;
};

export type OhtMessagesResult = {
  oht: OhtSummary;
  items: OhtMessage[];
};

export type CreateOhtMessageInput = {
  messageType: OhtMessageType;
  title: string;
  body: string;
  targetScope: OhtMessageTargetScope;
  selectedClinicIds?: number[];
  requiresAcknowledgement: boolean;
  expiresAt?: string | null;
};

export type OhtMessageRecipient = {
  ohtMessageAcknowledgementId: number;
  ohtMessageId: number;

  clinicId: number;
  clinicName: string;
  cityId: number | null;
  cityName: string | null;
  postalCode: string | null;
  normalizedFsa: string | null;

  acknowledged: boolean;
  acknowledgedAt: string | null;
  acknowledgedByUserId: number | null;
  acknowledgedByDisplayName: string | null;
  acknowledgedByEmail: string | null;

  responseText: string | null;
  respondedAt: string | null;
  respondedByUserId: number | null;
  respondedByDisplayName: string | null;
  respondedByEmail: string | null;
};

export type OhtMessageDetail = OhtMessage & {
  recipients: OhtMessageRecipient[];
};

function toApiMessageType(type: OhtMessageType | OhtMessageUiType): OhtMessageType {
  switch (type) {
    case "announcement":
      return "Announcement";
    case "policy_update":
      return "PolicyUpdate";
    case "capacity_request":
      return "CapacityRequest";
    case "operational_notice":
      return "OperationalNotice";
    default:
      return type;
  }
}

function toApiTargetScope(
  scope: OhtMessageTargetScope | OhtMessageUiScope
): OhtMessageTargetScope {
  switch (scope) {
    case "all":
      return "AllClinicsInOHT";
    case "accepting":
      return "AcceptingClinicsOnly";
    case "selected":
      return "SelectedClinics";
    default:
      return scope;
  }
}

function toUiMessageType(type: OhtMessageType | string): OhtMessageUiType {
  switch (type) {
    case "Announcement":
    case "announcement":
      return "announcement";
    case "PolicyUpdate":
    case "policy_update":
      return "policy_update";
    case "CapacityRequest":
    case "capacity_request":
      return "capacity_request";
    default:
      return "operational_notice";
  }
}

function toUiTargetScope(scope: OhtMessageTargetScope | string): OhtMessageUiScope {
  switch (scope) {
    case "AllClinicsInOHT":
    case "all":
      return "all";
    case "AcceptingClinicsOnly":
    case "accepting":
      return "accepting";
    default:
      return "selected";
  }
}

function normalizeOhtMessage(raw: any): OhtMessage {
  const messageType = toApiMessageType(raw?.messageType || raw?.type || "Announcement");
  const targetScope = toApiTargetScope(raw?.targetScope || raw?.scope || "all");

  return {
    ...raw,
    ohtMessageId: Number(raw?.ohtMessageId || raw?.id || 0),
    ohtId: Number(raw?.ohtId || 0),
    type: toUiMessageType(messageType),
    scope: toUiTargetScope(targetScope),
    sentAt: raw?.sentAt || raw?.createdAt || "",
    recipientsCount: Number(raw?.recipientsCount ?? raw?.recipientCount ?? 0),
    messageType,
    title: raw?.title || "",
    body: raw?.body || "",
    targetScope,
    requiresAcknowledgement: !!raw?.requiresAcknowledgement,
    expiresAt: raw?.expiresAt ?? null,
    isActive: raw?.isActive !== false,
    createdAt: raw?.createdAt || raw?.sentAt || "",
    createdByUserId: Number(raw?.createdByUserId || 0),
    createdByDisplayName: raw?.createdByDisplayName ?? null,
    createdByEmail: raw?.createdByEmail ?? null,
    recipientCount: Number(raw?.recipientCount ?? raw?.recipientsCount ?? 0),
    acknowledgedCount: Number(raw?.acknowledgedCount || 0),
    responseCount: Number(raw?.responseCount || 0),
  };
}

function normalizeCreateMessageInput(
  body: CreateOhtMessageInput
): CreateOhtMessageInput {
  return {
    ...body,
    messageType: toApiMessageType(body.messageType),
    targetScope: toApiTargetScope(body.targetScope),
  };
}

export async function getOhtMessages(
  signal?: AbortSignal
): Promise<OhtMessagesResult> {
  const result = await getResult<OhtMessagesResult>(
    "/api/portal/oht-admin/messages",
    signal
  );

  return {
    ...result,
    items: (result.items || []).map(normalizeOhtMessage),
  };
}

export async function createOhtMessage(
  body: CreateOhtMessageInput,
  signal?: AbortSignal
): Promise<OhtMessage> {
  const message = await postResult<OhtMessage>(
    "/api/portal/oht-admin/messages",
    normalizeCreateMessageInput(body),
    signal
  );

  return normalizeOhtMessage(message);
}

export async function getOhtMessage(
  messageId: number,
  signal?: AbortSignal
): Promise<OhtMessageDetail> {
  const detail = await getResult<OhtMessageDetail>(
    `/api/portal/oht-admin/messages/${messageId}`,
    signal
  );

  return {
    ...normalizeOhtMessage(detail),
    recipients: detail.recipients || [],
  };
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Last N calendar days as ISO date string (YYYY-MM-DD). Inclusive of today. */
export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Extract OHT scope from any response that carries meta.scope. */
export function extractOhtScope(res: {
  meta?: {
    scope?: {
      ohtId?: number | null;
      clinicIds?: number[];
      doctors?: LeadsScopeDoctor[] | null;
    };
  };
}): {
  ohtId: number | null;
  clinicIds: number[];
  doctors: LeadsScopeDoctor[];
} {
  const s = res?.meta?.scope;

  return {
    ohtId: s?.ohtId ?? null,
    clinicIds: Array.isArray(s?.clinicIds) ? s.clinicIds : [],
    doctors: Array.isArray(s?.doctors) ? s.doctors : [],
  };
}